'use client'

import { Children, useCallback, useRef, useState } from 'react'
import type { ReactNode } from 'react'

type WeightLossClinicCarouselProps = {
  children: ReactNode
  labels: string[]
}

// Mobile presentation of the clinic bento: a horizontal scroll-snap track with
// one card in view and the next peeking, plus a dot indicator that follows the
// scroll position. On desktop the parent renders the grid instead.
export function WeightLossClinicCarousel({
  children,
  labels,
}: WeightLossClinicCarouselProps) {
  const trackRef = useRef<HTMLUListElement>(null)
  const [active, setActive] = useState(0)
  const slides = Children.toArray(children)

  const handleScroll = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    const first = track.firstElementChild as HTMLElement | null
    const second = first?.nextElementSibling as HTMLElement | null
    if (!first) return
    const stride = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth
    if (stride <= 0) return
    const next = Math.min(
      slides.length - 1,
      Math.max(0, Math.round(track.scrollLeft / stride)),
    )
    setActive((current) => (current === next ? current : next))
  }, [slides.length])

  return (
    <div className="flex flex-col gap-6">
      <ul
        ref={trackRef}
        onScroll={handleScroll}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-pl-5 px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {slides.map((slide, index) => (
          <li
            key={labels[index] ?? index}
            className="w-[300px] shrink-0 snap-start"
            aria-label={labels[index]}
          >
            {slide}
          </li>
        ))}
      </ul>
      <ol
        className="flex items-center justify-center gap-1.5"
        aria-hidden="true"
      >
        {slides.map((_, index) => (
          <li
            key={index}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${
              index === active ? 'w-[18px] bg-[#0B0B0F]' : 'w-1.5 bg-[#D6D6DB]'
            }`}
          />
        ))}
      </ol>
    </div>
  )
}
